import React, { useEffect, useState } from "react";

import api from "../../Utils/api";
import { toast } from "react-toastify";

export default function PersonalElement({
  formData,
  onEdit,
  onMessage,
  onArchive,
}) {
  // Track which field is currently being edited
  const [editField, setEditField] = useState(null);
  // Local state for editable values
  const [localData, setLocalData] = useState(formData);

  // Handle input changes
  const handleChange = (field, value) => {
    setLocalData((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    setLocalData(formData);
  }, [formData]);

  // Handle blur or Enter key to finish editing
  const finishEditing = async () => {
    setEditField(null);
    let res = await api.post("/user/update", {
      _id: formData._id,
      updatedData: localData,
    });

    if (res.data.success) {
      toast.success(res.data.message);
    } else {
      toast.error(res.data.message);
    }

    // Optionally, call a prop function to save changes
    // onEdit(localData);
  };

  // Handle key press (Enter to save, Esc to cancel)
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      finishEditing();
    } else if (e.key === "Escape") {
      setLocalData(formData); // revert changes
      setEditField(null);
    }
  };

  return (
    <div className="tab-content w-full mt-8 px-20">
      {/* Personal Details */}
      <Section title="Personal details">
        <div className="grid grid-cols-4 gap-4 text-sm">
          <Field
            label="Name"
            value={`${localData.firstName} ${localData.lastName}`}
            editable={false} // complex field, not inline editable here
          />
          <Field
            label="Preferred name"
            value={localData.preferredName}
            editable
            editing={editField === "preferredName"}
            onClick={() => setEditField("preferredName")}
            onChange={(v) => handleChange("preferredName", v)}
            onBlur={finishEditing}
            onKeyDown={handleKeyDown}
          />
          <Field
            label="Pronouns"
            value={localData.pronouns || "Not specified"}
            editable
            editing={editField === "pronouns"}
            onClick={() => setEditField("pronouns")}
            onChange={(v) => handleChange("pronouns", v)}
            onBlur={finishEditing}
            onKeyDown={handleKeyDown}
          />
          <Field
            label="Date of birth"
            value={localData.birthday || "Add a date of birth"}
            editable
            editing={editField === "birthday"}
            inputType="date"
            onClick={() => setEditField("birthday")}
            onChange={(v) => handleChange("birthday", v)}
            onBlur={finishEditing}
            onKeyDown={handleKeyDown}
          />
        </div>
      </Section>

      {/* Contact Info */}
      <Section title="Contact">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <Field
            label="Email"
            value={localData.email}
            editing={editField === "email"}
            onClick={() => setEditField("email")}
            onChange={(v) => handleChange("email", v)}
            onBlur={finishEditing}
            onKeyDown={handleKeyDown}
          />
          <Field
            label="Mobile"
            value={localData.phone || "Add a mobile"}
            editable
            editing={editField === "phone"}
            onClick={() => setEditField("phone")}
            onChange={(v) => handleChange("phone", v)}
            onBlur={finishEditing}
            onKeyDown={handleKeyDown}
          />
          <Field
            label="Address"
            value={localData.address || "Add an address"}
            editable
            editing={editField === "address"}
            onClick={() => setEditField("address")}
            onChange={(v) => handleChange("address", v)}
            onBlur={finishEditing}
            onKeyDown={handleKeyDown}
          />
        </div>
      </Section>
    </div>
  );
}

// Section wrapper
const Section = ({ title, children }) => (
  <div className="mb-8 mt-20">
    <h2 className="text-[30px] font-semibold text-gray-900 mb-4">{title}</h2>
    <div className="rounded-lg p-6 bg-[#f9f9fb]">{children}</div>
  </div>
);

// Editable Field component
const Field = ({
  label,
  value,
  editable = false,
  editing = false,
  onClick,
  onChange,
  onBlur,
  onKeyDown,
  inputType = "text",
}) => {
  return (
    <div className="mb-4">
      <p className="text-gray-500 font-medium mb-1">{label}</p>
      {editable && editing ? (
        <input
          type={inputType}
          autoFocus
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500"
        />
      ) : (
        <p
          onClick={editable ? onClick : undefined}
          className={`${
            editable
              ? "text-gray-600 cursor-pointer hover:underline"
              : "text-gray-900"
          }`}
          tabIndex={editable ? 0 : undefined}
          onKeyDown={(e) => {
            if (editable && (e.key === "Enter" || e.key === " ")) {
              onClick();
            }
          }}
          role={editable ? "button" : undefined}
          aria-label={editable ? `Edit ${label}` : undefined}
        >
          {value || (editable ? `Add ${label.toLowerCase()}` : "-")}
        </p>
      )}
    </div>
  );
};
